import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LogOut, Home as HomeIcon, User, Bell, UserCheck, UserX, FileText, Users as UsersIcon, Calendar as CalendarIcon, Car, MapPin, BookOpen, MessageSquarePlus, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import FeedbackWidget from "@/components/FeedbackWidget";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

export default function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/pending");
      setPending(data.pending || []);
    } catch { /* ignore */ }
  }, []);

  const loadNotifs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadPending();
    loadNotifs();
    const id = setInterval(() => { loadPending(); loadNotifs(); }, 30000);
    return () => clearInterval(id);
  }, [loadPending, loadNotifs]);

  // Refresh notifs when menu opens & mark read
  useEffect(() => {
    if (menuOpen) {
      loadNotifs();
      // Mark all as read after a small delay (so badge clears)
      const t = setTimeout(async () => {
        try { await api.post("/notifications/read-all"); setUnread(0); } catch { /* ignore */ }
      }, 800);
      return () => clearTimeout(t);
    }
  }, [menuOpen, loadNotifs]);

  const approveFromMenu = async (groupId, userId) => {
    try {
      await api.post(`/groups/${groupId}/approve/${userId}`);
      toast.success("Membro approvato");
      await loadPending();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectFromMenu = async (groupId, userId) => {
    try {
      await api.post(`/groups/${groupId}/reject/${userId}`);
      toast.success("Richiesta rifiutata");
      await loadPending();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, []);
  return (
    <div className="min-h-screen pb-24 md:pb-8 relative">
      {/* White header with subtle border */}
      <header className="relative bg-white border-b border-[#ECEEF5]">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-2 pb-2 flex items-center justify-between gap-3">
          <Link to="/home" className="flex items-center group" data-testid="header-logo">
            <img src="/hopzigo-logo.png" alt="Hopzigo" className="h-24 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-2">
            <NavLink to="/home" className={({isActive}) => `px-4 py-2 rounded-xl font-semibold text-sm transition-all ${isActive ? "bg-[#1A2A6C] text-white shadow-[0_4px_14px_rgba(26,42,108,0.20)]" : "text-[#1A2A6C] hover:bg-[#F5F6FA]"}`} data-testid="nav-home">
              Home
            </NavLink>
            <NavLink to="/calendar" className={({isActive}) => `px-4 py-2 rounded-xl font-semibold text-sm transition-all ${isActive ? "bg-[#1A2A6C] text-white shadow-[0_4px_14px_rgba(26,42,108,0.20)]" : "text-[#1A2A6C] hover:bg-[#F5F6FA]"}`} data-testid="nav-dashboard">
              Calendario
            </NavLink>
            <NavLink to="/profile" className={({isActive}) => `px-4 py-2 rounded-xl font-semibold text-sm transition-all ${isActive ? "bg-[#1A2A6C] text-white shadow-[0_4px_14px_rgba(26,42,108,0.20)]" : "text-[#1A2A6C] hover:bg-[#F5F6FA]"}`} data-testid="nav-profile">
              Profilo
            </NavLink>
            <button onClick={logout} className="w-10 h-10 grid place-items-center rounded-xl text-[#1A2A6C] hover:bg-[#F5F6FA]" data-testid="nav-logout" title="Esci">
              <LogOut className="w-4 h-4" />
            </button>
          </nav>
          <div className="md:hidden relative flex items-center gap-2" ref={menuRef}>
            <LanguageSwitcher />
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="relative w-14 h-14 grid place-items-center rounded-full bg-[#1A2A6C] text-white font-heading font-bold text-2xl shadow-[0_4px_14px_rgba(26,42,108,0.20)] hover:bg-[#14205A]"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              data-testid="header-user-avatar"
            >
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
              {(pending.length + unread) > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 grid place-items-center rounded-full text-[10px] font-bold bg-[#FF4D5E] text-white border-2 border-white animate-pulse" data-testid="avatar-notif-badge">
                  {pending.length + unread}
                </span>
              )}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-16 z-40 w-80 max-w-[calc(100vw-2rem)] rounded-2xl bg-white border border-[#ECEEF5] shadow-[0_10px_30px_rgba(20,32,80,0.12)] overflow-hidden anim-pop-in" role="menu">
                <div className="px-4 py-3 border-b border-[#ECEEF5]">
                  <p className="font-semibold text-sm text-[#1A1D29] truncate">{user?.name}</p>
                  <p className="text-xs text-[#8A91A8] truncate">{user?.email}</p>
                </div>

                {/* Notifications panel */}
                <div className="border-b border-[#ECEEF5]" data-testid="menu-notifications-panel">
                  <div className="px-4 py-2.5 flex items-center justify-between bg-[#F5F6FA]">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#1A2A6C]" />
                      <span className="text-xs font-bold uppercase tracking-widest text-[#1A2A6C]">{t("menu.notifications")}</span>
                    </div>
                    {(pending.length + unread) > 0 && (
                      <span className="brut-chip bg-[#FFE3E3] text-[#FF4D5E]">{pending.length + unread}</span>
                    )}
                  </div>

                  {/* Pending admin approvals (priority) */}
                  {pending.length > 0 && (
                    <ul className="max-h-48 overflow-y-auto border-b border-[#ECEEF5]" data-testid="menu-pending-list">
                      {pending.map((p) => (
                        <li key={`${p.group_id}-${p.user_id}`} className="px-3 py-2.5 border-b border-[#F5F6FA] last:border-0" data-testid={`menu-pending-${p.user_id}`}>
                          <div className="flex items-start gap-2">
                            <div className="w-8 h-8 grid place-items-center rounded-full bg-[#FFE9C2] text-[#B07512] font-heading font-bold text-sm shrink-0">
                              {p.user_name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold leading-tight truncate">{p.user_name}</p>
                              <p className="text-xs text-[#8A91A8] truncate">vuole entrare in "{p.group_name}"</p>
                            </div>
                          </div>
                          <div className="flex gap-1.5 mt-2 pl-10">
                            <button onClick={() => approveFromMenu(p.group_id, p.user_id)} className="flex-1 px-2 py-1.5 rounded-lg bg-[#26B5A8] text-white text-xs font-semibold hover:bg-[#1F9D91] inline-flex items-center justify-center gap-1" data-testid={`menu-approve-${p.user_id}`}>
                              <UserCheck className="w-3.5 h-3.5" /> Approva
                            </button>
                            <button onClick={() => rejectFromMenu(p.group_id, p.user_id)} className="flex-1 px-2 py-1.5 rounded-lg bg-white border border-[#FFD1D6] text-[#FF4D5E] text-xs font-semibold hover:bg-[#FFE3E3] inline-flex items-center justify-center gap-1" data-testid={`menu-reject-${p.user_id}`}>
                              <UserX className="w-3.5 h-3.5" /> Rifiuta
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* General activity notifications */}
                  {notifs.length === 0 && pending.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-[#8A91A8] text-center">{t("menu.noNotifications")}</p>
                  ) : (
                    <ul className="max-h-64 overflow-y-auto" data-testid="menu-notif-list">
                      {notifs.map((n) => (
                        <li
                          key={n.id}
                          className={`px-3 py-2.5 border-b border-[#F5F6FA] last:border-0 cursor-pointer hover:bg-[#F5F6FA] ${!n.read ? "bg-[#F0F4FF]" : ""}`}
                          onClick={() => { setMenuOpen(false); navigate(n.url || "/home"); }}
                          data-testid={`menu-notif-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D5E] mt-1.5 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold leading-tight text-[#1A1D29] truncate">{n.title}</p>
                              <p className="text-xs text-[#5A6178] mt-0.5 line-clamp-2">{n.body}</p>
                              <p className="text-[10px] text-[#8A91A8] mt-0.5">
                                {n.created_at ? new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                              </p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button
                  onClick={() => { setMenuOpen(false); navigate("/profile"); }}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#1A2A6C] hover:bg-[#F5F6FA] inline-flex items-center gap-2"
                  data-testid="mobile-menu-profile"
                >
                  <User className="w-4 h-4" /> {t("menu.profile")}
                </button>
                <a
                  href="/hopzigo-termini-condizioni.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#1A2A6C] hover:bg-[#F5F6FA] inline-flex items-center gap-2 border-t border-[#ECEEF5]"
                  data-testid="mobile-menu-terms"
                >
                  <FileText className="w-4 h-4" /> {t("menu.terms")}
                </a>
                <button
                  onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("hopzigo:open-tutorial")); }}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#1A2A6C] hover:bg-[#F5F6FA] inline-flex items-center gap-2 border-t border-[#ECEEF5]"
                  data-testid="mobile-menu-tutorial"
                >
                  <BookOpen className="w-4 h-4" /> {t("menu.tutorial")}
                </button>
                <NavLink
                  to="/places"
                  onClick={() => setMenuOpen(false)}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#1A2A6C] hover:bg-[#F5F6FA] inline-flex items-center gap-2 border-t border-[#ECEEF5]"
                  data-testid="mobile-menu-places"
                >
                  <Star className="w-4 h-4 text-[#FF9E1B]" /> {t("menu.places")}
                </NavLink>
                <button
                  onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("hopzigo:open-feedback")); }}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#1A2A6C] hover:bg-[#F5F6FA] inline-flex items-center gap-2 border-t border-[#ECEEF5]"
                  data-testid="mobile-menu-feedback"
                >
                  <MessageSquarePlus className="w-4 h-4" /> {t("menu.feedback")}
                </button>
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full text-left px-4 py-3 text-sm font-semibold text-[#FF4D5E] hover:bg-[#FFE3E3] inline-flex items-center gap-2 border-t border-[#ECEEF5]"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-4 h-4" /> {t("menu.logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content - no overlap to avoid white-text bleed */}
      <main className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-8">
        <Outlet />
      </main>

      {/* Feedback floating button - available on every authenticated page */}
      <FeedbackWidget />

      {/* Mobile bottom nav - colorful logo-gradient stripe with centered Home FAB + 4 quick links */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30" data-testid="bottom-nav">
        <div className="relative h-20">
          {/* Colored stripe with logo colors */}
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, #1A2A6C 0%, #26B5A8 33%, #FF9E1B 66%, #FF4D5E 100%)",
            }}
          />
          {/* 4 quick links in 5-col grid (center col reserved for Home FAB) */}
          <div className="absolute inset-0 grid grid-cols-5 items-center text-white">
            <NavLink to="/groups" className="flex flex-col items-center gap-0.5 text-white/95 hover:text-white" data-testid="bottomnav-groups" aria-label="I miei gruppi">
              <UsersIcon className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold leading-none whitespace-nowrap">{t("nav.groups")}</span>
            </NavLink>
            <NavLink to="/calendar" className="flex flex-col items-center gap-0.5 text-white/95 hover:text-white" data-testid="bottomnav-calendar" aria-label="Il mio calendario">
              <CalendarIcon className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold leading-none whitespace-nowrap">{t("nav.calendar")}</span>
            </NavLink>
            <div aria-hidden="true" />
            <NavLink to="/rides?filter=mine" className="flex flex-col items-center gap-0.5 text-white/95 hover:text-white" data-testid="bottomnav-myrides" aria-label="Le mie corse">
              <Car className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold leading-none whitespace-nowrap">{t("nav.myRides")}</span>
            </NavLink>
            <NavLink to="/rides?filter=available" className="flex flex-col items-center gap-0.5 text-white/95 hover:text-white" data-testid="bottomnav-available" aria-label="Corse disponibili">
              <MapPin className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-[10px] font-bold leading-none whitespace-nowrap">{t("nav.available")}</span>
            </NavLink>
          </div>
          {/* Centered Home FAB notched on top of the stripe */}
          <div className="absolute inset-x-0 -top-6 flex justify-center pointer-events-none">
            <NavLink
              to="/home"
              className="pointer-events-auto w-16 h-16 grid place-items-center rounded-full bg-white text-[#1A2A6C] shadow-[0_8px_22px_rgba(20,32,80,0.35)] ring-4 ring-white"
              data-testid="bottomnav-home"
              aria-label="Home"
            >
              <HomeIcon className="w-7 h-7" strokeWidth={2.5} />
            </NavLink>
          </div>
        </div>
      </nav>
    </div>
  );
}
